import type { MarketingAttribution } from "@/types";

const STORAGE_KEY = "qlt_marketing_attribution_v1";
const TTL_MS = 90 * 24 * 60 * 60 * 1_000;

const CLICK_ID_FIELDS = ["gclid", "gbraid", "wbraid", "msclkid"] as const;
const CAMPAIGN_FIELDS = [
  ["gad_campaignid", "campaignId"],
  ["campaignid", "campaignId"],
  ["utm_id", "campaignId"],
  ["utm_campaign", "campaignName"],
  ["utm_source", "utmSource"],
  ["utm_medium", "utmMedium"],
  ["utm_term", "utmTerm"],
  ["utm_content", "utmContent"]
] as const;

const VALUE_LIMITS: Record<string, number> = {
  gclid: 512,
  gbraid: 512,
  wbraid: 512,
  msclkid: 512,
  campaignId: 128,
  campaignName: 256,
  utmSource: 128,
  utmMedium: 128,
  utmTerm: 256,
  utmContent: 256
};

type AttributionLocation = Pick<Location, "hostname" | "pathname">;
type CaptureStorage = Pick<Storage, "setItem">;
type ReadStorage = Pick<Storage, "getItem">;

function bounded(value: unknown, limit: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, limit) : undefined;
}

function readAllowedFields(value: Record<string, unknown>): Partial<MarketingAttribution> {
  const result: Partial<MarketingAttribution> = {};
  for (const key of CLICK_ID_FIELDS) {
    const accepted = bounded(value[key], VALUE_LIMITS[key]);
    if (accepted) result[key] = accepted;
  }
  for (const [, key] of CAMPAIGN_FIELDS) {
    if (result[key]) continue;
    const accepted = bounded(value[key], VALUE_LIMITS[key]);
    if (accepted) result[key] = accepted;
  }
  return result;
}

/**
 * Capture one complete tagged-landing bundle. A later tagged visit replaces the
 * prior bundle so click IDs and campaign fields from different visits cannot mix.
 */
export function captureMarketingAttribution(
  search: string,
  location: AttributionLocation,
  storage: CaptureStorage,
  now: number
): void {
  const params = new URLSearchParams(search || "");
  const captured: Partial<MarketingAttribution> = {};

  for (const key of CLICK_ID_FIELDS) {
    const value = bounded(params.get(key), VALUE_LIMITS[key]);
    if (value) captured[key] = value;
  }
  for (const [parameter, key] of CAMPAIGN_FIELDS) {
    if (captured[key]) continue;
    const value = bounded(params.get(parameter), VALUE_LIMITS[key]);
    if (value) captured[key] = value;
  }

  if (Object.keys(captured).length === 0) return;

  const record: MarketingAttribution = {
    ...captured,
    landingHostname: bounded(location.hostname, 255) || "unknown",
    landingPath: bounded(location.pathname, 1_024) || "/",
    capturedAt: new Date(now).toISOString(),
    model: "last_attributed_landing_90d"
  };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Attribution is best-effort and must never block booking or startup.
  }
}

/** Return the current non-expired bundle, re-validating stored browser data. */
export function readMarketingAttribution(
  storage: ReadStorage,
  now: number
): MarketingAttribution | undefined {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const capturedAt = bounded(parsed.capturedAt, 64);
    const capturedTime = capturedAt ? Date.parse(capturedAt) : Number.NaN;
    if (!Number.isFinite(capturedTime) || capturedTime > now || now - capturedTime >= TTL_MS) {
      return undefined;
    }

    const accepted = readAllowedFields(parsed);
    if (Object.keys(accepted).length === 0) return undefined;

    return {
      ...accepted,
      landingHostname: bounded(parsed.landingHostname, 255) || "unknown",
      landingPath: bounded(parsed.landingPath, 1_024) || "/",
      capturedAt: new Date(capturedTime).toISOString(),
      model: "last_attributed_landing_90d"
    };
  } catch {
    return undefined;
  }
}
